import { QueryClient, SetDataOptions, Updater } from "@tanstack/react-query";
import { FORCE_INVALIDATE } from "../constants";
import {
  MethodForModel,
  ModelName,
  ParamsForMethod,
  QueryDataForMethod,
  QueryMethodForModel,
  TellescopeQueryUpdate,
  TellescopeQueryUpdateWithTimestamp,
} from "../../types";
import { buildTellescopeQueryKey } from "../../keys";
import { getTellescopeQueryState } from "./get-tellescope-query-state";

/**
 * Arguments for setting query data in cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 */
export interface SetTellescopeQueryDataArgs<Model extends ModelName, Method extends MethodForModel<Model>> {
  /** The Tellescope model name */
  model: Model;
  /** The query method to call on the model */
  method: Method;
  /** The request parameters for the query */
  request: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The data to set, or an updater function */
  updater: Updater<QueryDataForMethod<Model, Method> | undefined, QueryDataForMethod<Model, Method> | undefined>;
  /** Optional set data options (e.g., updatedAt) */
  options?: SetDataOptions;
}

/**
 * Return type for setting query data in cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 */
export interface SetTellescopeQueryDataReturn<Model extends ModelName, Method extends QueryMethodForModel<Model>> {
  /** The query that was updated with its original data before the update */
  originalQuery: TellescopeQueryUpdateWithTimestamp<Model, Method>;
  /** The query that was updated with its new data after the update */
  updatedQuery: TellescopeQueryUpdate<Model, Method>;
}

/**
 * Sets query data in the query client cache for a Tellescope query.
 * Can be used to update the cache directly without making a network request.
 * For infinite/paginated queries, data should be wrapped in InfiniteData structure.
 *
 * **Special Updater Return Values:**
 * - Return `undefined`: Skip the update (no change to cache)
 * - Return `null`: Reset the query (remove from cache) - useful for deletions
 * - Return `FORCE_INVALIDATE`: Invalidate the query (triggers a network refetch)
 * - Return data: Update the cache with the new data
 *
 * @param args - The set query data arguments
 * @returns An object containing the original query data before update and the updated query data
 *
 * @example
 * ```tsx
 * // Set data directly
 * const { originalQuery, updatedQuery } = setTellescopeQueryData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   updater: { id: "user-123", fname: "John", lname: "Doe", email: "john@example.com" },
 * });
 * // originalQuery: [queryKey, oldData, timestamp]
 * // updatedQuery: [queryKey, newData]
 *
 * // Update existing data with an updater function
 * const result = setTellescopeQueryData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   updater: (old) => old ? { ...old, fname: "Jane" } : undefined,
 * });
 *
 * // Reset/delete query data (useful for deletions)
 * setTellescopeQueryData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "deleted-user-123" },
 *   updater: () => null, // Resets the query
 * });
 *
 * // Set infinite/paginated query data
 * setTellescopeQueryData({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   request: { filter: {}, limit: 10 },
 *   updater: {
 *     pages: [[{ id: "user-1", fname: "John", lname: "Doe", email: "john@example.com" }]],
 *     pageParams: [undefined],
 *   },
 * });
 *
 * // Set data with custom options
 * setTellescopeQueryData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 *   updater: { id: "user-123", fname: "Jane", lname: "Doe", email: "jane@example.com" },
 *   options: {
 *     updatedAt: Date.now(), // Set custom update timestamp
 *   },
 * });
 * ```
 */
export const setTellescopeQueryData = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model> = QueryMethodForModel<Model>,
>({
  model,
  method,
  request,
  queryClient,
  updater,
  options,
}: SetTellescopeQueryDataArgs<Model, Method>): SetTellescopeQueryDataReturn<Model, Method> => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  let originalData: QueryDataForMethod<Model, Method> | undefined;
  let shouldReset = false;
  let shouldInvalidate = false;

  const wrappedUpdater: Updater<
    QueryDataForMethod<Model, Method> | undefined,
    QueryDataForMethod<Model, Method> | undefined
  > = (old) => {
    originalData = old;
    // @ts-expect-error - TypeScript can't narrow union types when QueryData could itself be a function
    const updatedData = typeof updater === "function" ? updater(old) : updater;

    if (updatedData === FORCE_INVALIDATE) {
      shouldInvalidate = true;
      return old;
    }

    if (updatedData === null) {
      shouldReset = true;
    }

    return updatedData;
  };

  const originalUpdatedAt = getTellescopeQueryState({
    model,
    method,
    request,
    queryClient,
  })?.dataUpdatedAt;

  // First, call the updater to determine if we should reset/invalidate and capture original data
  const currentData = queryClient.getQueryData<QueryDataForMethod<Model, Method>>(queryKey);
  wrappedUpdater(currentData);

  let updatedData: QueryDataForMethod<Model, Method> | undefined;

  if (shouldInvalidate) {
    // Invalidate the query when updater returns FORCE_INVALIDATE
    void queryClient.invalidateQueries({ queryKey, exact: true });
    updatedData = undefined;
  } else if (shouldReset) {
    // Reset the query when updater returns null
    queryClient.resetQueries({ queryKey, exact: true });
    updatedData = undefined;
  } else {
    // Otherwise, apply the update normally
    updatedData = queryClient.setQueryData<QueryDataForMethod<Model, Method>>(queryKey, wrappedUpdater, options);
  }

  return {
    originalQuery: [queryKey, originalData, originalUpdatedAt],
    updatedQuery: [queryKey, updatedData],
  };
};
